// gti-chms-frontend/src/components/Toast.tsx
import React, { useEffect, useState } from 'react';

interface ToastProps {
    id: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    duration?: number; // How long the toast stays visible
    onDismiss: (id: string) => void; // Callback to remove the toast from state
}

const Toast: React.FC<ToastProps> = ({ id, message, type, duration = 5000, onDismiss }) => {
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        // Set a timer to automatically dismiss the toast
        const timer = setTimeout(() => {
            setIsVisible(false); // Start fade-out animation
            // After fade-out, actually remove from the DOM
            setTimeout(() => onDismiss(id), 300); // 300ms matches CSS transition duration
        }, duration);

        return () => clearTimeout(timer); // Clean up timer on unmount
    }, [duration, id, onDismiss]);

    // Tailwind CSS classes for different toast types
    const typeClasses = {
        success: 'bg-green-500',
        error: 'bg-red-500',
        info: 'bg-blue-500',
        warning: 'bg-yellow-500',
    };

    // Combine classes for animation and styling
    const containerClasses = `
        px-6 py-3 rounded-lg shadow-lg text-white flex items-center justify-between
        transition-opacity duration-300 transform
        ${typeClasses[type]}
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
    `;

    return (
        <div className={containerClasses}>
            <p className="text-sm font-medium mr-4">{message}</p>
            <button
                onClick={() => {
                    setIsVisible(false); // Trigger fade-out on manual dismiss
                    setTimeout(() => onDismiss(id), 300);
                }}
                className="ml-auto text-white focus:outline-none"
                aria-label="Dismiss toast"
            >
                {/* Close icon */}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
        </div>
    );
};

export default Toast;
